import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, AlertTriangle, X, MessageSquare } from 'lucide-react';
import { floatingSurfaceClass } from '../floatingSurfaceStyles';
import {
  SEVERITY_COLORS, SEVERITY_LABELS, relativeTime, eventTypeLabel,
  commodityTags, placeLabel, alertStart,
} from './alertModel';

const SURFACE = floatingSurfaceClass(97);

/** 300px per the design; the collapsed pill is 40px tall. */
const PANEL_W = 'w-[300px]';

/** Ask BiQ — square icon button, right-aligned in the card's bottom row. */
const AskBiqButton = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className="ml-auto flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-[7px] border border-[#E2E6E3] bg-white text-[#5D625F] transition-colors hover:border-bloom-green hover:text-bloom-green dark:border-[#2A302C] dark:bg-[#232726] dark:text-[#9AA09A]"
  >
    <MessageSquare size={12} />
  </button>
);

/** Commodity / count chip. */
const Tag = ({ children }) => (
  <span className="rounded-[6px] bg-[#F3F5F2] px-[7px] py-[2px] font-['Inter'] text-[11px] font-semibold whitespace-nowrap text-[#5D625F] dark:bg-[#252B27] dark:text-[#9AA09A]">
    {children}
  </span>
);

/**
 * Weather card: severity-coloured left border, type + severity + timing, then
 * title, description and the commodity tags.
 */
const WeatherAlertCard = ({ alert, onAskBiq, t }) => {
  const severity = alert._severity;
  const colour = SEVERITY_COLORS[severity];
  const type = eventTypeLabel(alert);
  const when = relativeTime(alert._start ?? alertStart(alert));
  const tags = commodityTags(alert);
  const place = placeLabel(alert);
  const title = alert.title || place || type || t('map.alerts.untitled', 'Weather alert');

  return (
    <article
      className="border-l-[3px] border-solid py-[10px] pl-[11px] pr-[12px]"
      style={{ borderColor: colour }}
    >
      <div className="flex flex-wrap items-center gap-x-[8px] gap-y-[2px]">
        {type && (
          <span className="font-['Inter'] text-[10.5px] font-extrabold tracking-[0.4px] text-[#282A29] dark:text-[#E6E8E6]">
            {type}
          </span>
        )}
        <span className="flex items-center gap-[4px]">
          <span className="h-[6px] w-[6px] shrink-0 rounded-full" style={{ backgroundColor: colour }} />
          <span className="font-['Inter'] text-[11px] font-semibold" style={{ color: colour }}>
            {t(`map.alerts.severity.${severity}`, SEVERITY_LABELS[severity])}
          </span>
        </span>
        {when && (
          <span className="ml-auto font-['Inter'] text-[11px] whitespace-nowrap text-[#828382]">
            {when}
          </span>
        )}
      </div>

      <h4 className="mt-[5px] font-['Inter'] text-[12.5px] font-bold leading-[17px] text-[#282A29] dark:text-[#E6E8E6]">
        {title}
      </h4>

      {place && title !== place && (
        <p className="mt-[2px] font-['Inter'] text-[11px] text-[#828382]">{place}</p>
      )}

      {alert.impact_summary && (
        <p className="mt-[4px] font-['Inter'] text-[11.5px] leading-[16px] text-[#5D625F] dark:text-[#9AA09A]">
          {alert.impact_summary}
        </p>
      )}

      {(tags.length > 0 || onAskBiq) && (
        <div className="mt-[8px] flex flex-wrap items-center gap-[6px]">
          {tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}
          {onAskBiq && (
            <AskBiqButton
              label={t('map.alerts.askBiq', 'Ask BiQ about this alert')}
              onClick={() => onAskBiq(alert)}
            />
          )}
        </div>
      )}
    </article>
  );
};

/**
 * Region card: deliberately neutral — no left border, no icon, no severity —
 * so it reads as information rather than a warning.
 */
const RegionAlertCard = ({ region, onAskBiq, t, statusLabel }) => {
  const place = placeLabel(region) || region.region || region.country;
  const when = relativeTime(alertStart(region.alert) ?? alertStart(region));
  const weeks = region.alert?.weeks_delta;
  const n = weeks == null ? null : Number(weeks);
  const hasDelta = n != null && Number.isFinite(n) && n !== 0;

  return (
    <article className="py-[10px] pl-[14px] pr-[12px]">
      <div className="flex items-center gap-[8px]">
        <span className="font-['Inter'] text-[10.5px] font-extrabold tracking-[0.4px] text-[#5D625F] dark:text-[#9AA09A]">
          {statusLabel}
        </span>
        {when && (
          <span className="ml-auto font-['Inter'] text-[11px] whitespace-nowrap text-[#828382]">
            {when}
          </span>
        )}
      </div>

      <h4 className="mt-[5px] font-['Inter'] text-[12.5px] font-bold leading-[17px] text-[#282A29] dark:text-[#E6E8E6]">
        {place}
      </h4>

      {/* The delta is the point of the card, so it carries the bold the design
          asks for; the message is the sentence around it. */}
      {hasDelta && (
        <p className="mt-[3px] font-['Inter'] text-[11.5px] leading-[16px] text-[#5D625F] dark:text-[#9AA09A]">
          <strong className="font-bold text-[#282A29] dark:text-[#E6E8E6]">
            {Math.abs(n)} {Math.abs(n) === 1 ? t('map.alerts.week', 'week') : t('map.alerts.weeks', 'weeks')}
          </strong>
          {' '}
          {n < 0 ? t('map.alerts.ahead', 'ahead of last season') : t('map.alerts.behind', 'behind last season')}
        </p>
      )}

      {region.alert?.message && (
        <p className="mt-[3px] font-['Inter'] text-[11.5px] leading-[16px] text-[#5D625F] dark:text-[#9AA09A]">
          {region.alert.message}
        </p>
      )}

      {region.commodity && (
        <p className="mt-[3px] font-['Inter'] text-[11px] text-[#828382]">
          {region.commodity}
        </p>
      )}

      {onAskBiq && (
        <div className="mt-[8px] flex items-center">
          <AskBiqButton
            label={t('map.alerts.askBiq', 'Ask BiQ about this region')}
            onClick={() => onAskBiq(region)}
          />
        </div>
      )}
    </article>
  );
};

/** Underlined tab with a count badge. */
const Tab = ({ active, label, count, onClick }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={`flex items-center gap-[6px] border-b-2 border-solid px-[4px] pb-[7px] font-['Inter'] text-[12px] font-semibold transition-colors ${
      active
        ? 'border-bloom-green text-[#282A29] dark:text-[#E6E8E6]'
        : 'border-transparent text-[#828382] hover:text-[#5D625F]'
    }`}
  >
    {label}
    <span
      className={`font-['Inter'] text-[11px] font-extrabold ${
        active ? 'text-[#16A34A]' : 'text-[#9CA3AF]'
      }`}
    >
      {count}
    </span>
  </button>
);

/**
 * The map's Alerts control: a 300×40 pill that expands in place into a tabbed
 * panel.
 *
 * `open` is local. Nothing outside needs to know whether the list is showing,
 * and routing it through the page would add a prop that only ever comes back
 * here. The default tab, however, follows the layer — Weather mode opens on
 * Weather, Growing on Regions — so it is derived from a prop.
 */
const MapAlertsPanel = ({
  weatherAlerts = [],
  regionAlerts = [],
  defaultTab = 'weather',
  onAskBiq = null,
  regionStatusLabel = (r) => String(r?.harvest_status || '').replace(/_/g, ' ').toUpperCase(),
  className = '',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(defaultTab);
  // Phones show the open panel as a bottom sheet. A transform only animates
  // from a value the browser has already painted, so the sheet mounts
  // translated down and is raised on the next frame.
  const [sheetIn, setSheetIn] = useState(false);
  useEffect(() => {
    if (!open) {
      setSheetIn(false);
      return undefined;
    }
    const id = requestAnimationFrame(() => setSheetIn(true));
    return () => cancelAnimationFrame(id);
  }, [open]);
  const listRef = useRef(null);

  const total = weatherAlerts.length + regionAlerts.length;

  // The layer decides which tab is meaningful, so a mode switch re-picks it.
  useEffect(() => { setTab(defaultTab); }, [defaultTab]);

  // "Switching tabs scrolls the list back to the top" — otherwise the new tab
  // inherits the old one's scroll offset and opens part-way down.
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = 0; }, [tab]);

  const countLabel = t('map.alerts.activeCount', '{{count}} active', { count: total });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={false}
        // Content width on phones, fixed from lg — same reasoning as the
        // Arrivals pill beside it, and the two must agree or they stack
        // ragged.
        className={`flex h-[40px] w-fit max-w-full shrink-0 cursor-pointer items-center gap-[8px] rounded-[20px] border border-solid py-0 pl-[13px] pr-[6px] lg:w-[300px] ${SURFACE} ${className}`}
      >
        <AlertTriangle size={16} className="shrink-0 text-[#DC2626] dark:text-[#F87171]" />
        <span className="font-['Inter'] text-[13px] font-bold whitespace-nowrap text-[#343440] dark:text-[#E6E8E6]">
          {t('map.alerts.title', 'Alerts')}
        </span>
        <span className="font-['Inter'] text-[13px] font-extrabold whitespace-nowrap text-[#B91C1C] dark:text-[#F87171]">
          {countLabel}
        </span>
        <span className="ml-auto flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full bg-[#F3F5F2] dark:bg-[#252B27]">
          <ChevronDown size={13} className="text-[#5D625F] dark:text-[#9AA09A]" />
        </span>
      </button>
    );
  }

  const rows = tab === 'weather' ? weatherAlerts : regionAlerts;

  return (
    <div
      className={`flex ${PANEL_W} max-w-full min-h-0 flex-col overflow-hidden rounded-[16px] border border-solid ${SURFACE} ${className}
        max-lg:fixed max-lg:inset-x-0 max-lg:bottom-0 max-lg:z-[1500] max-lg:w-full max-lg:max-w-none
        max-lg:max-h-[78svh] max-lg:rounded-b-none max-lg:rounded-t-[20px] max-lg:border-b-0
        max-lg:transition-transform max-lg:duration-300 max-lg:ease-out max-lg:motion-reduce:transition-none
        ${sheetIn ? 'max-lg:translate-y-0' : 'max-lg:translate-y-full'}`}
    >
      {/* Grab bar. Phones only — it is what marks this as a sheet rather than
          a panel that happens to be docked at the bottom. Decorative: the
          close button in the header is the real control. */}
      <span
        aria-hidden="true"
        className="mx-auto mt-[8px] mb-[2px] block h-[4px] w-[36px] shrink-0 rounded-full bg-[#D5DAD6] lg:hidden dark:bg-[#3A413C]"
      />
      {/* Header — fixed */}
      <div className="flex shrink-0 items-center gap-[8px] py-[10px] pl-[13px] pr-[8px]">
        <AlertTriangle size={16} className="shrink-0 text-[#DC2626] dark:text-[#F87171]" />
        <span className="font-['Inter'] text-[13px] font-bold whitespace-nowrap text-[#343440] dark:text-[#E6E8E6]">
          {t('map.alerts.title', 'Alerts')}
        </span>
        <span className="rounded-[8px] bg-[#FEF2F2] px-[7px] py-[2px] font-['Inter'] text-[11.5px] font-extrabold whitespace-nowrap text-[#B91C1C] dark:bg-[#3A1F1F] dark:text-[#F87171]">
          {countLabel}
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label={t('map.alerts.close', 'Close alerts')}
          className="ml-auto flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full bg-[#F3F5F2] text-[#5D625F] transition-colors hover:text-[#282A29] dark:bg-[#252B27] dark:text-[#9AA09A]"
        >
          <X size={13} />
        </button>
      </div>

      {/* Tabs — fixed */}
      <div role="tablist" className="flex shrink-0 items-center gap-[16px] border-b border-solid border-[#EEF0EC] px-[13px] dark:border-[#2A302C]">
        <Tab
          active={tab === 'weather'}
          label={t('map.alerts.tabs.weather', 'Weather')}
          count={weatherAlerts.length}
          onClick={() => setTab('weather')}
        />
        <Tab
          active={tab === 'regions'}
          label={t('map.alerts.tabs.regions', 'Regions')}
          count={regionAlerts.length}
          onClick={() => setTab('regions')}
        />
      </div>

      {/* List — the only scrolling part, so the header and tabs stay put.
          `min-h-0` lets it shrink inside the flex column instead of pushing
          the panel past the height the page allows it. */}
      <div
        ref={listRef}
        className="map-alerts-scroll min-h-0 flex-1 divide-y divide-[#F1F3F0] overflow-y-auto dark:divide-[#242A26]"
      >
        {rows.length === 0 ? (
          <p className="px-[13px] py-[14px] font-['Inter'] text-[11.5px] text-[#828382]">
            {tab === 'weather'
              ? t('map.alerts.noWeather', 'No active weather alerts.')
              : t('map.alerts.noRegions', 'No region alerts.')}
          </p>
        ) : tab === 'weather' ? (
          rows.map((a, i) => (
            <WeatherAlertCard
              key={`${a.country || ''}-${a.specific_alert_type || a.title || ''}-${i}`}
              alert={a}
              onAskBiq={onAskBiq}
              t={t}
            />
          ))
        ) : (
          rows.map((r, i) => (
            <RegionAlertCard
              key={`${r.region || ''}-${r.commodity || ''}-${i}`}
              region={r}
              onAskBiq={onAskBiq}
              statusLabel={regionStatusLabel(r)}
              t={t}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default MapAlertsPanel;
